import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatChartDate, formatInr } from "@/lib/intelligenceFormat";

type TrendPoint = { date: string; revenue: number; opdVisits: number };

type Props = {
  revenueTrend: TrendPoint[];
  title?: string;
};

export function DoctorPerformanceChart({ revenueTrend, title = "Revenue trend" }: Props) {
  const chartData = useMemo(
    () => ({
      labels: revenueTrend.map((p) => formatChartDate(p.date)),
      datasets: [
        {
          label: "Revenue (OPD payments)",
          data: revenueTrend.map((p) => p.revenue),
          borderColor: "rgb(79, 70, 229)",
          backgroundColor: "rgba(79, 70, 229, 0.08)",
          tension: 0.25,
          fill: true,
        },
        {
          label: "OPD Visits",
          data: revenueTrend.map((p) => p.opdVisits),
          borderColor: "rgb(16, 185, 129)",
          tension: 0.25,
          fill: false,
        },
      ],
    }),
    [revenueTrend]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { position: "bottom" as const },
      },
      scales: {
        y: {
          ticks: {
            callback: (v: string | number) =>
              Number(v) >= 1000 ? formatInr(Number(v)) : String(v),
          },
        },
      },
    }),
    []
  );

  if (revenueTrend.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">No trend data</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">{title}</CardTitle>
        <p className="text-xs text-muted-foreground">
          OPD payment revenue by day (appointment-linked). IPD not included in daily trend.
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[260px]">
          <Line data={chartData} options={options} />
        </div>
      </CardContent>
    </Card>
  );
}
